import { useQueries } from '@tanstack/react-query'
import { api } from '../../lib/api.ts'
import { useExtensoesInstaladas, type ExtensaoInstalada } from './useExtensoes.ts'

/**
 * Selos que os plugins põem nos cards (SPEC-20260926-2036-plugin-acao-e-selo).
 *
 * Três regras que decidem se isto fica usável ou vira o motivo de o quadro
 * ficar lento:
 *
 * 1. **Em lote.** Uma chamada por extensão, com todos os ids do quadro — nunca
 *    uma por card. Um quadro tem centenas.
 * 2. **Nunca bloqueia o render.** O card desenha sem selo e preenche quando a
 *    resposta chega. Nada aqui é esperado por ninguém.
 * 3. **Falha em silêncio.** Selo é enfeite informativo; plugin fora do ar não
 *    pode impedir alguém de ver o quadro. O servidor já devolve lista vazia.
 */

export type Selo = {
  workItemId: string
  texto: string
  tone: 'neutral' | 'primary' | 'success' | 'warning' | 'danger'
  titulo?: string
}

/** Os selos de cada Item, já juntados de todos os plugins. */
export type SelosPorItem = ReadonlyMap<string, Selo[]>

const VAZIO: SelosPorItem = new Map()

/**
 * `ids` costuma ser recriado a cada render do quadro. A chave da consulta usa
 * a lista ORDENADA e unida — assim reordenar ou reembaralhar os cards não
 * dispara chamada nova ao plugin.
 */
const chaveDosIds = (ids: readonly string[]) => [...ids].sort().join(',')

export function useSelosDePlugin(workItemIds: readonly string[], ligado: boolean): SelosPorItem {
  const extensoes = useExtensoesInstaladas('card-badge', ligado).data ?? []
  const chave = chaveDosIds(workItemIds)

  const consultas = useQueries({
    queries: extensoes.map((e: ExtensaoInstalada) => ({
      queryKey: ['plugins', 'badges', e.pluginKey, e.chave, chave],
      enabled: ligado && workItemIds.length > 0,
      // o TTL é do manifesto: o plugin sabe melhor que nós com que frequência
      // o dado dele muda
      staleTime: (e.config.ttlSeconds ?? 300) * 1000,
      gcTime: (e.config.ttlSeconds ?? 300) * 1000 * 2,
      // plugin fora do ar não vira tela de erro num quadro
      retry: false,
      queryFn: async () =>
        (
          (await (
            await api.plugins.badges[':chave'].$post({
              param: { chave: e.chave },
              json: { workItemIds: [...workItemIds] },
            })
          ).json()) as { selos: Selo[] }
        ).selos,
    })),
  })

  if (extensoes.length === 0) return VAZIO

  const porItem = new Map<string, Selo[]>()
  for (const c of consultas) {
    for (const selo of c.data ?? []) {
      const lista = porItem.get(selo.workItemId)
      if (lista) lista.push(selo)
      else porItem.set(selo.workItemId, [selo])
    }
  }
  return porItem
}
